import React from 'react';
import NextStepActions from '../NextStepActions';
import type { ConnectionPhase } from '../../services/connectionJourneyService';

interface NextStepModalProps {
  isOpen: boolean;
  onClose: () => void;
  nextPhaseCandidate: ConnectionPhase | null;
  connectionName: string;
  onDirectCall: (phase?: 'call' | 'video' | 'physical') => void;
  onAbortFlow: () => void;
  onPlanAgenda: () => void;
  onEndWithoutPhysical: (reason: string) => void;
}

// Fase configuratie - centrale bron van waarheid
const PHASE_CONFIG: Partial<Record<ConnectionPhase, { name: string; icon: string }>> = {
  call: { name: 'Belafspraak', icon: '📞' },
  video: { name: 'Videogesprek', icon: '📹' },
  physical: { name: 'Fysieke afspraak', icon: '🏢' },
  completed: { name: 'Verbonden', icon: '✅' },
};

const NextStepModal: React.FC<NextStepModalProps> = ({
  isOpen,
  onClose,
  nextPhaseCandidate,
  connectionName,
  onDirectCall,
  onAbortFlow,
  onPlanAgenda,
  onEndWithoutPhysical
}) => {
  if (!isOpen) return null;

  // Dynamisch de fase naam bepalen op basis van nextPhaseCandidate
  const getNextPhaseName = (): string => {
    if (!nextPhaseCandidate || nextPhaseCandidate === 'chat') {
      return 'Volgende stap';
    }
    return PHASE_CONFIG[nextPhaseCandidate]?.name ?? 'Volgende stap';
  };

  const nextPhaseName = getNextPhaseName();

  return (
    <div 
      className="fixed inset-0 bg-black/50 backdrop-blur-sm flex items-center justify-center z-[999] p-4 transition-opacity duration-200"
    >
      <div 
        className="bg-white rounded-2xl shadow-2xl border border-gray-200 w-full max-w-md p-6 space-y-4 transform transition-all duration-200 ease-out translate-y-1 opacity-100"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="flex items-start justify-between">
          <div>
            <h3 className="text-lg font-semibold text-gray-900">Volgende stap</h3>
            <p className="text-sm text-gray-600">
              Kies hoe je verder gaat met deze connectie
            </p>
          </div>
          <button
            onClick={onClose}
            className="p-2 rounded-full hover:bg-gray-100 text-gray-500"
          >
            ✕
          </button>
        </div>

        <div className="bg-blue-50 border border-blue-200 rounded-lg p-3 text-sm text-gray-700">
          <div className="font-semibold text-blue-900 mb-1">
            {nextPhaseCandidate && nextPhaseCandidate !== 'chat'
              ? `Volgende stap: ${nextPhaseName}`
              : 'Geen volgende fase (einde traject)'}
          </div>
          <div className="text-xs text-blue-700">
            Hoe wil je de volgende fase oppakken?
          </div>
        </div>

        <NextStepActions
          disabled={!nextPhaseCandidate || nextPhaseCandidate === 'chat'}
          connectionName={connectionName}
          nextPhaseName={nextPhaseName}
          nextPhaseCandidate={nextPhaseCandidate}
          onDirectCall={onDirectCall}
          onAbortFlow={onAbortFlow}
          onPlanAgenda={onPlanAgenda}
          onEndWithoutPhysical={onEndWithoutPhysical}
          onClose={onClose}
        />
      </div>
    </div>
  );
};

export default NextStepModal;
